import { useState, Dispatch } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import dayjs from 'dayjs';
import TableLayout from '../TableLayout';
import GrayBox from '../common/GrayBox';
import { Stack } from 'components/Stack';
import { Text } from 'components/Typography';
import { Button } from 'components/Button';
import theme from 'constants/theme';
import { QueueSelectorWrapper } from 'pages/Report/utils/ReportFilter';
import { ReportFilterContent } from 'pages/Report/utils/ReportFilterContent';
import AutoTableComponent from './AutoTableComponent';
import { useSelectingRow } from '../common/CheckSelectStore';
import { useSingleAutoMessage } from './AutoStore';
import { getMessageAuto } from '../api';
import { dtoAutoMessage } from '../dto';
import { TRequestFilter } from './index';

export interface IAutoMessage {
  code: string; // 메시지코드(id)
  status: boolean; // 활성상태
  receiver: 'driver' | 'client'; // 수신인 (고객 | 기사 | 중개사 | 차주)
  process: string; //진행구분 (시작 | 중간 | 완료 | 1차보고 | 최종보고)
  title: string; //메시지제목
  trigger: string; // 발송시점
  contents: string; // 매시지 내용
  disabledSendTime: boolean; // 발송시간 비활성화
  sendStartTime: string; // 발송시작시간
  sendEndTime: string; // 발송종료시간
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  processStatus?: string | null;
}

const getAutoSettingList = async (params: TRequestFilter) => {
  const realData = await getMessageAuto({ receiverType: params.recipientType });
  if (Array.isArray(realData)) {
    return dtoAutoMessage(realData);
  } else {
    return [];
  }
};

type Props = {
  requestFilter: TRequestFilter;
  setRequestFilter: Dispatch<TRequestFilter>;
};

const AutoTable = ({ requestFilter, setRequestFilter }: Props) => {
  const { setselectRow } = useSelectingRow();
  const { setSingleMessage } = useSingleAutoMessage();
  const [RecipientTypeFilter, setRecipientTypeFilter] = useState([
    { key: '전체', value: 'all', isSelected: true },
    { key: '고객', value: 'client', isSelected: false },
    { key: '기사', value: 'driver', isSelected: false },
    { key: '중개사', value: 'salesman', isSelected: false },
    { key: '화주사', value: 'consignor', isSelected: false },
  ]);

  const onClickSearch = () => {
    const recipientType = RecipientTypeFilter.find(d => d.isSelected)?.value || 'all';
    setRequestFilter({ recipientType });
    // 조회시 row 선택 초기화
    setselectRow('');
    setSingleMessage(null);
  };

  const {
    data: autoMessageList,
    refetch: autoMessageListRefetch,
    isFetching,
  } = useQuery(['autoMessageList', requestFilter], () => getAutoSettingList(requestFilter), {
    onSuccess: res => {
      // console.log(res);
    },
    enabled: !!requestFilter,
  });
  return (
    <TableLayout
      title="알림메시지 자동 설정"
      desc="주문 상태 별, 수신인 종류 별로 자동 발송할 알림메시지를 미리 작성하고 설정할 수 있습니다."
    >
      <Stack spacing={40}>
        <GrayBox
          sx={{
            borderRadius: '0px 12px 12px',
            flexDirection: 'row',
            padding: '12px 16px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack
            name="sub-filter-wrapper"
            sx={{
              margin: '20px 0 0 0',
              minHeight: 'fit-content',
              maxHeight: '270px',
              transition: 'all .6s',
            }}
            style={{ alignItems: 'start', gap: 6 }}
          >
            <ReportFilterContent
              title="수신인 종류"
              altInfo={`수신인 종류 필터입니다.`}
              anchor="bottom"
              direction="row"
              gap={20}
            >
              <QueueSelectorWrapper direction={'row'} sx={{ width: '448px', marginLeft: '12px' }}>
                {RecipientTypeFilter.map((d, index) => {
                  return (
                    <QueueSelectorContainerCustom
                      key={d.key}
                      selected={d.isSelected}
                      onClick={() => {
                        setRecipientTypeFilter(
                          RecipientTypeFilter.map((item, idx) => {
                            return { ...item, isSelected: index === idx };
                          })
                        );
                        setRequestFilter({ recipientType: d.value });
                        // 조회시 row 선택 초기화
                        setselectRow('');
                        setSingleMessage(null);
                      }}
                      // disabled={['중개사', '화주사'].includes(d.key)}
                      width={78}
                    >
                      <Text styleName={'caption2'} color={d.isSelected ? 'RC02' : 'RG02'}>
                        {d.key}
                      </Text>
                    </QueueSelectorContainerCustom>
                  );
                })}
              </QueueSelectorWrapper>
            </ReportFilterContent>
          </Stack>
          {/* <Stack style={{ alignItems: 'end' }}>
            <Button type="button" variant="secondary" h={32} onClick={onClickSearch}>
              조회하기
            </Button>
          </Stack> */}
        </GrayBox>
      </Stack>

      <Stack sx={{ marginTop: 20 }}>
        <AutoTableComponent
          data={autoMessageList ?? []}
          isFetching={isFetching}
          refetchdata={() => {
            // console.log('refetch');
          }}
          requestFilter={requestFilter}
          autoMessageListRefetch={autoMessageListRefetch}
        />
      </Stack>
    </TableLayout>
  );
};
export default AutoTable;

const QueueSelectorContainerCustom = styled.button<{ width: number; selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  min-width: 64px;

  width: ${({ width }) => width}px;
  height: 100%;

  padding: 6px;
  white-space: nowrap;
  position: relative;

  ${({ selected }) =>
    selected
      ? {
          backgroundColor: theme.colors.RC03_1,
          border: `1px solid ${theme.colors.RC03}`,
          zIndex: 1,
        }
      : { backgroundColor: theme.colors.RG00, border: `1px solid ${theme.colors.RG06}` }}
  &:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  &:not(:last-child) {
    margin-right: -1px;
  }

  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  &:disabled {
    background-color: #eceef4;
    border-right-color: 1px solid ${theme.colors.RG06};
    border-top-color: 1px solid ${theme.colors.RG06};
    border-bottom-color: 1px solid ${theme.colors.RG06};
    color: #6e7280;

    cursor: not-allowed;
  }

  cursor: pointer;
`;
